import { useEffect, useState } from "react";
import { Loader2, Info, X, User, Phone } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PatientSearchDropdown } from "@/components/messages/patient-search-dropdown";
import { HealoChannelGlyph, WhatsappChannelGlyph } from "@/components/messages/healo-channel-glyph";
import { maskPhoneInput, phoneDigitCount, validatePhoneInput } from "@/lib/phone";

// The Healo-app channel is parked for now — new conversations go out over WhatsApp only.
// Flip back to `true` to restore the channel picker and everything behind it.
const APP_CHANNEL_ENABLED = false;

function patientName(patient) {
  return (
    patient?.displayName
    || `${patient?.profile?.FirstName || ""} ${patient?.profile?.LastName || ""}`.trim()
    || "Unknown Patient"
  );
}

function patientPhone(patient) {
  if (patient?._selectedPhone) return String(patient._selectedPhone).trim();
  const phones = patient?.contacts?.Phones;
  if (Array.isArray(phones) && phones.length > 0) {
    const verified = phones.find((p) => p.Verified);
    return (verified || phones[0])?.E164 || "";
  }
  return "";
}

function appLastSeenTitle(patient) {
  const raw = patient?.appLastSeenAt;
  if (raw) {
    const date = new Date(raw);
    if (!Number.isNaN(date.getTime())) {
      return `Last active in the Healo app ${date.toLocaleString()}`;
    }
  }
  return "Has signed in to the Healo app";
}

/**
 * Start a conversation with a patient who hasn't messaged first, on either patient channel:
 *
 * WhatsApp — pick an existing patient (and optionally a specific phone), or type a new
 * number directly (the backend find-or-creates a patient by that number, so an inbound
 * reply lands on the same thread), and choose which business number to send from when
 * several are configured. WhatsApp restricts first-contact to approved templates outside
 * the 24h window — the hint surfaces that; the gateway bot handles template selection.
 *
 * Healo app — PARKED behind `APP_CHANNEL_ENABLED`: the channel picker is hidden and every
 * conversation starts on WhatsApp. When re-enabled: existing patients only, and only those
 * who have signed in from the mobile app at least once (`appConnected` from the patient
 * search); the Healo icon on the chip confirms the message will be delivered in the app.
 *
 * `onStart` finds-or-creates the channel's thread and opens it.
 *
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {(args: { patientUserId?: string, phone?: string, displayName?: string, whatsappBotId?: string, channel: "whatsapp"|"app" }) => Promise<any>} props.onStart
 * @param {{ botId: string, label: string }[]} [props.whatsappNumbers] Configured business numbers.
 */
export function NewPatientMessageDialog({ open, onOpenChange, onStart, whatsappNumbers = [] }) {
  // Which patient channel the conversation starts on.
  const [channel, setChannel] = useState("whatsapp");
  // "patient" = search the directory; "phone" = type a bare number (WhatsApp only).
  const [mode, setMode] = useState("patient");
  const [selected, setSelected] = useState(null);
  // Empty, not prefilled with "+65": a prefill concatenates with a pasted number that
  // already carries the country code, producing an id no WhatsApp account answers to.
  const [phoneInput, setPhoneInput] = useState("");
  const [phoneName, setPhoneName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [selectedBotId, setSelectedBotId] = useState("");

  useEffect(() => {
    if (!open) return;
    setChannel("whatsapp");
    setMode("patient");
    setSelected(null);
    setPhoneInput("");
    setPhoneName("");
    setSubmitting(false);
    setSubmitError("");
  }, [open]);

  // Default the send-from number to the first configured one, keeping it valid as the list loads.
  useEffect(() => {
    if (!Array.isArray(whatsappNumbers) || whatsappNumbers.length === 0) return;
    setSelectedBotId((prev) =>
      prev && whatsappNumbers.some((n) => n.botId === prev) ? prev : whatsappNumbers[0].botId,
    );
  }, [whatsappNumbers]);

  const isAppChannel = APP_CHANNEL_ENABLED && channel === "app";
  const name = selected ? patientName(selected) : "";
  const phone = selected ? patientPhone(selected) : "";
  // "Has the app": the patient signed in from healo_app at least once (server-stamped).
  const appConnected = Boolean(selected?.appConnected);
  const phoneCheck = validatePhoneInput(phoneInput);
  const phoneReady = phoneCheck.ok;
  // Only explain the problem once there are enough digits to judge one, so the hint does
  // not flash on every keystroke while the number is still being typed.
  const phoneHint = !phoneCheck.ok && phoneDigitCount(phoneInput) >= 8 ? phoneCheck.message : "";
  const canSubmit = submitting
    ? false
    : isAppChannel
      ? Boolean(selected?.id) && appConnected
      : mode === "phone"
        ? phoneReady
        : Boolean(selected?.id) && Boolean(phone);

  const selectChannel = (next) => {
    setChannel(next);
    setSubmitError("");
    // The app channel needs an existing account — there is no "new number" there.
    if (next === "app" && mode === "phone") setMode("patient");
  };

  const handleStart = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      if (isAppChannel) {
        await onStart?.({
          patientUserId: String(selected.id),
          displayName: name,
          channel: "app",
        });
      } else if (mode === "phone") {
        await onStart?.({
          phone: phoneInput.trim(),
          displayName: phoneName.trim() || undefined,
          whatsappBotId: selectedBotId,
          channel: "whatsapp",
        });
      } else {
        await onStart?.({
          patientUserId: String(selected.id),
          phone,
          displayName: name,
          whatsappBotId: selectedBotId,
          channel: "whatsapp",
        });
      }
      onOpenChange?.(false);
    } catch (error) {
      setSubmitError(error?.message || "Could not start the conversation. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New message</DialogTitle>
          <DialogDescription>
            {isAppChannel
              ? "Message a patient in their Healo app."
              : "Start a WhatsApp conversation with a patient."}
          </DialogDescription>
        </DialogHeader>

        {/* Channel selector: which patient channel the conversation starts on.
            Hidden while the app channel is parked — WhatsApp is the only option. */}
        {APP_CHANNEL_ENABLED ? (
          <div className="inline-flex w-full rounded-lg border border-border bg-muted/40 p-0.5">
            {[
              { key: "whatsapp", label: "WhatsApp", Glyph: WhatsappChannelGlyph },
              { key: "app", label: "Healo app", Glyph: HealoChannelGlyph },
            ].map(({ key, label, Glyph }) => (
              <button
                key={key}
                type="button"
                onClick={() => selectChannel(key)}
                disabled={submitting}
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                  channel === key
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Glyph className="h-3.5 w-3.5 shrink-0" />
                {label}
              </button>
            ))}
          </div>
        ) : null}

        {/* Mode toggle: search an existing patient, or type a new number (WhatsApp only —
            the app channel needs an existing signed-in account). */}
        {!isAppChannel ? (
          <div className="inline-flex w-full rounded-lg border border-border bg-muted/40 p-0.5">
            {[
              { key: "patient", label: "Existing patient", Icon: User },
              { key: "phone", label: "New number", Icon: Phone },
            ].map(({ key, label, Icon }) => (
              <button
                key={key}
                type="button"
                onClick={() => setMode(key)}
                disabled={submitting}
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                  mode === key
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </button>
            ))}
          </div>
        ) : null}

        {mode === "phone" && !isAppChannel ? (
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="new-number-name" className="text-xs font-medium text-muted-foreground">
                Name <span className="font-normal">(optional)</span>
              </label>
              <Input
                id="new-number-name"
                value={phoneName}
                onChange={(e) => setPhoneName(e.target.value)}
                disabled={submitting}
                placeholder="Patient name"
                autoComplete="off"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="new-number-phone" className="text-xs font-medium text-muted-foreground">
                Phone number
              </label>
              <Input
                id="new-number-phone"
                value={phoneInput}
                onChange={(e) => setPhoneInput(maskPhoneInput(e.target.value))}
                disabled={submitting}
                inputMode="tel"
                autoFocus
                className="font-mono"
                placeholder="+6591234567"
              />
              {phoneHint ? (
                <p className="text-[11px] font-medium text-destructive">{phoneHint}</p>
              ) : null}
            </div>
          </div>
        ) : selected ? (
          <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/40 px-3 py-2.5">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-[11px] font-bold text-amber-700 ring-1 ring-amber-400/30 dark:bg-amber-500/20 dark:text-amber-300">
              {name.split(" ").slice(0, 2).map((p) => p[0]?.toUpperCase() || "").join("")}
            </div>
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm font-medium text-foreground">{name}</span>
              {phone ? <span className="font-mono text-[11px] text-muted-foreground">{phone}</span> : null}
            </div>
            {isAppChannel && appConnected ? (
              // Deliverability confirmation: this patient has the Healo app.
              <span
                className="inline-flex shrink-0 items-center gap-1 rounded-full bg-sky-500/10 px-2 py-0.5 text-[11px] font-medium text-sky-700 dark:text-sky-300"
                title={appLastSeenTitle(selected)}
              >
                <HealoChannelGlyph className="h-3 w-3 shrink-0" />
                Healo app
              </span>
            ) : null}
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted"
              title="Choose a different patient"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <PatientSearchDropdown onSelectPatient={setSelected} autoFocus label="Find patient" />
        )}

        {!isAppChannel && mode === "patient" && selected && !phone ? (
          <p className="text-sm text-destructive">This patient has no phone number to message.</p>
        ) : null}
        {isAppChannel && selected && !appConnected ? (
          <p className="text-sm text-destructive">
            This patient hasn&apos;t used the Healo app yet, so a message can&apos;t reach them there.
            Try WhatsApp instead.
          </p>
        ) : null}

        {!isAppChannel && whatsappNumbers.length > 1 ? (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="wa-send-from" className="text-xs font-medium text-muted-foreground">
              Send from
            </label>
            <select
              id="wa-send-from"
              value={selectedBotId}
              onChange={(e) => setSelectedBotId(e.target.value)}
              disabled={submitting}
              className="h-9 w-full rounded-md border border-border bg-background px-2.5 text-sm text-foreground shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
            >
              {whatsappNumbers.map((n) => (
                <option key={n.botId} value={n.botId}>
                  {n.label}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {!isAppChannel ? (
          <div className="flex items-start gap-2 rounded-md bg-amber-500/10 px-3 py-2 text-[11px] text-amber-700 dark:text-amber-300">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>The first message to a new contact may require an approved WhatsApp template.</span>
          </div>
        ) : (
          <div className="flex items-start gap-2 rounded-md bg-sky-500/10 px-3 py-2 text-[11px] text-sky-700 dark:text-sky-300">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              The app has no push notifications yet — the patient sees the message the next time
              they open Healo.
            </span>
          </div>
        )}

        {submitError ? <p className="text-sm text-destructive">{submitError}</p> : null}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange?.(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button type="button" onClick={handleStart} disabled={!canSubmit}>
            {submitting ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            Start conversation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default NewPatientMessageDialog;
